import React, { useMemo } from 'react';
import classNames from 'classnames';
import { format } from 'date-fns';

import { isDesktop } from 'src/utils/devices';
import { PendingEditsType } from 'src/features/hosLogs/hosLog/types';
import { ELD_DRIVER_STATUS, statusToString } from 'src/constants/ELDConstants';
import { formatStartTime, getDateWithoutTimezone, millisecondsToHours } from 'src/utils/dateUtils';
import { styleByStatus } from 'src/components/HosEventListItem/HosEventListItem';
import { round } from 'src/features/hosLogs/hosEditLog/utils';

interface PendingEditsTableProps {
  pendingEdits: PendingEditsType;
  allowOdometerEdits: boolean;
}

export const PendingEditsTable: React.FC<PendingEditsTableProps> = ({
  pendingEdits,
  allowOdometerEdits,
}) => {
  const adminEdits = useMemo(() => pendingEdits.pendingEditsList[0], [pendingEdits]);

  const insertedHosGridObject = useMemo(
    () => adminEdits.hosGridObject.find(item => item.inserted),
    [adminEdits],
  );

  const footerString = `Proposed by ${adminEdits.editedBy.employeeName} on ${format(
    getDateWithoutTimezone(adminEdits.dateEdited),
    'MMM do yyyy',
  )}.
                        ${
                          insertedHosGridObject && insertedHosGridObject.adminEditNotes
                            ? `Edit note: "${insertedHosGridObject.adminEditNotes}"`
                            : ''
                        }`;

  return (
    <>
      {adminEdits.exceptions && adminEdits.exceptions.length > 0 && (
        <>
          {isDesktop && (
            <tbody className="log-info-table-thead">
              <tr>
                <th>Status</th>
                <th colSpan={2}>Exception</th>
                <th>Location</th>
                <th colSpan={2}>Note</th>
                <th />
              </tr>
            </tbody>
          )}
          <tbody className="log-info-tbody-content">
            {adminEdits.exceptions.map(exception => {
              return (
                <tr
                  className={classNames('log-info-box', { 'is-desktop': isDesktop })}
                  key={exception.shorthand}
                >
                  <td className="log-info-header log-info-header__exc">{exception.shorthand}</td>
                  <td data-title="Exception" colSpan={2}>
                    {exception.readableExceptionType}
                  </td>
                  <td data-title="Location">{exception.driverLocation}</td>
                  <td data-title="Note" colSpan={2}>
                    {exception.notes}
                  </td>
                  <td />
                </tr>
              );
            })}
          </tbody>
        </>
      )}

      {isDesktop && (
        <tbody className="log-info-table-thead">
          <tr>
            <th>Status</th>
            <th>Start Time</th>
            <th>Duration</th>
            <th>Location</th>
            <th>PC/YM</th>
            <th>Note</th>
            {allowOdometerEdits && <th>Odometer</th>}
            <th />
          </tr>
        </tbody>
      )}
      <tbody className="log-info-tbody-pending-edits">
        {adminEdits.hosGridObject.map(hosGridObject => {
          const {
            status,
            time,
            notes,
            location,
            pcymStatus,
            duration,
            odometer,
            editedOdometer,
          } = hosGridObject;
          const odometerValue = odometer
            ? round(odometer) + ' mi'
            : editedOdometer
            ? round(editedOdometer) + ' mi'
            : '--';
          return (
            <tr className={classNames('log-info-box', { 'is-desktop': isDesktop })} key={time}>
              <td
                className={classNames(
                  'log-info-header',
                  styleByStatus[status as ELD_DRIVER_STATUS],
                )}
              >
                {statusToString(status)}
              </td>
              <td data-title="Start Time">{time ? formatStartTime(time) : '--'}</td>
              <td data-title="Duration">{millisecondsToHours(duration)} HRS</td>
              <td data-title="Location">{location ? location : '--'}</td>
              <td data-title="PC/YM">{pcymStatus ? pcymStatus : '--'}</td>
              <td data-title="Note">{notes ? notes : '--'}</td>
              {allowOdometerEdits && <td data-title="Odometer">{odometerValue}</td>}
              <td className="no-padding" />
            </tr>
          );
        })}
      </tbody>
      <tbody className="log-info-tbody-content">
        {isDesktop ? (
          <tr>
            <td colSpan={8}>
              <div className="hos-log__admin-edit-pending__footer">
                <div>{footerString}</div>
              </div>
            </td>
          </tr>
        ) : (
          <tr
            className={classNames('log-info-box', 'log-info-box__mobile-footer', {
              'is-desktop': isDesktop,
            })}
          >
            {insertedHosGridObject && insertedHosGridObject.adminEditNotes && (
              <td data-title="Edit notes">{insertedHosGridObject.adminEditNotes}</td>
            )}
            <td data-title="Proposed by">{adminEdits.editedBy.employeeName}</td>
            <td data-title="Proposed at">
              {format(getDateWithoutTimezone(adminEdits.dateEdited), 'MMM do yyyy')}
            </td>
          </tr>
        )}
      </tbody>
    </>
  );
};
